<script>
    import { onMount } from "svelte";
    import { writable } from "svelte/store";
    import {
        currentUser,
        selectedItem,
        redovisning,
        answer,
        question,
    } from "../../store/store.js";
    import {
        fetchBrf,
        getFileName,
        displayOwnsTheLand,
        slugify,
    } from "../../utils/utils.js";
    import Header from "../../components/Header.svelte";
    import Logotype from "../../components/Logotype.svelte";
    import { Link } from "svelte-routing";
    import Search from "../../components/Search.svelte";

    // Import all the analysis components
    import General from "../../components/boxes/redovisning/General.svelte";
    import Chatbot from "../../components/Bobot.svelte";
    import Rating from "../../components/Rating/Rating.svelte";
    import Property from "../../components/boxes/redovisning/Property.svelte";
    import Maintenance from "../../components/boxes/redovisning/Maintenance.svelte";
    import EconomyAndIncomes from "../../components/boxes/redovisning/EconomyAndIncomes.svelte";
    import Company from "../../components/boxes/Company.svelte";
    import ReaVinstCalculator from "../../components/boxes/ReaVinstCalculator.svelte";
    import FinancialPlan from "../../components/boxes/FinancialPlan.svelte";
    import UploadRedovisning from "../../components/boxes/UploadRedovisning.svelte";
    import PropertyMap from "../../components/PropertyMap.svelte";
    import LoanFeeHistory from "../../components/boxes/history/LoanFeeHistory.svelte";
    import ExpensesHistory from "../../components/boxes/history/ExpensesHistory.svelte";
    import IncomeHistory from "../../components/boxes/history/IncomeHistory.svelte";
    // Stadgar
    import Stadgar from "../../components/Rating/Stadgar.svelte";
    import StadgarFAQ from "../../components/Rating/StadgarFAQ.svelte";
    import AnalyzeStadgar from "../../components/AnalyzeStadgar.svelte";

    // Energideklaration
    import Energy from "../../components/Rating/Energy.svelte";
    import AnalyzeEnergy from "../../components/AnalyzeEnergy.svelte";

    // Ekonomisk Plan
    import AnalyzeFinancialPlan from "../../components/AnalyzeFinancialPlan.svelte";

    // This is how we get the ID parameter from svelte-routing
    export let id;
    let loading = true;
    let error = null;
    let activeTab = writable("allmant"); // Tracks the active tab: 'allmant', 'stadgar', or 'energi'

    // Helper function to create URL-friendly slugs

    let showIncomeHistory = false;
    let showLoanFeeHistory = false;
    let showExpensesHistory = false;
    let showHistoryCharts = false;
    $: {
        const history = $selectedItem?.core_history?.history_by_year;
        if (history) {
            showHistoryCharts = Object.keys(history).length >= 3;
            showIncomeHistory = Object.values(history).some(
                (yearData) =>
                    (yearData.rental_income_from_commercial_premises !== null &&
                        yearData.rental_income_from_commercial_premises !==
                            undefined) ||
                    (yearData.rental_income_from_parking_spots !== null &&
                        yearData.rental_income_from_parking_spots !==
                            undefined) ||
                    (yearData.rental_income_from_rental_apartments !== null &&
                        yearData.rental_income_from_rental_apartments !==
                            undefined),
            );
            showLoanFeeHistory = Object.values(history).some(
                (yearData) =>
                    (yearData.annual_fee_income !== null &&
                        yearData.annual_fee_income !== undefined) ||
                    (yearData.total_amount_of_loans !== null &&
                        yearData.total_amount_of_loans !== undefined),
            );
            showExpensesHistory = Object.values(history).some(
                (yearData) =>
                    (yearData.total_maintenance_expenses !== null &&
                        yearData.total_maintenance_expenses !== undefined) ||
                    (yearData.total_operating_expenses !== null &&
                        yearData.total_operating_expenses !== undefined) ||
                    (yearData.other_external_costs !== null &&
                        yearData.other_external_costs !== undefined),
            );
        } else {
            showIncomeHistory = false;
            showHistoryCharts = false;
        }
    }

    // Function to change the active tab
    function selectTab(tabName) {
        activeTab.set(tabName);
    }

    // Reactive SEO variables
    let pageTitle = "Boanalys";
    let metaDescription =
        "Boanalys erbjuder marknadens mest djupgående analyser av bostadsrättsföreningar.";
    let canonicalUrl = "https://boanalys.io/";
    let jsonLdData = null;
    let productJsonLdData = null;
    let breadcrumbJsonLdData = null;
    let faqJsonLdData = null;

    // Set initial canonical URL based on current path
    // This ensures canonical is set correctly even before data loads
    if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/brf/')) {
            // If URL already has slug, use it as canonical (preferred)
            if (currentPath.match(/^\/brf\/\d+\/[^/]+$/)) {
                canonicalUrl = `https://boanalys.io${currentPath}`;
            } else if (currentPath.match(/^\/brf\/\d+$/)) {
                // If URL doesn't have slug, canonical will be updated to include slug when data loads
                // For now, set a temporary canonical that will be replaced
                canonicalUrl = `https://boanalys.io${currentPath}`;
            }
        }
    }

    async function loadBrfData(brfId) {
        if (!brfId) return;
        // --- Nollställ chatbotten ---
        answer.set("");
        question.set("");
        // --- Slut nollställning ---
        try {
            loading = true;

            // Fetch the BRF data using the ID from the URL
            const result = await fetchBrf(brfId);

            if (!result) {
                error =
                    "Kunde inte ladda information om bostadsrättsföreningen.";
                console.error("Failed to load BRF data, result was:", result);
            }

            // Once data is loaded, update SEO metadata
            if ($selectedItem && $selectedItem.brf_name) {
                // Update file name for redovisning if needed
                if ($selectedItem.fileName) {
                    redovisning.set($selectedItem.fileName);
                }

                // Update page title - more compelling for CTR
                pageTitle = `${$selectedItem.brf_name} - Gratis Analys & Betyg | Boanalys`;

                // More compelling description with specific data points for higher CTR
                let descriptionText = `Gratis analys av ${$selectedItem.brf_name}`;
                
                if ($selectedItem.municipality) {
                    descriptionText += ` i ${$selectedItem.municipality}`;
                }
                
                descriptionText += `. `;
                
                // Add compelling data points
                if ($selectedItem.built_year) {
                    descriptionText += `Byggår ${$selectedItem.built_year}. `;
                }
                if ($selectedItem.number_of_apartments) {
                    descriptionText += `${$selectedItem.number_of_apartments} lägenheter. `;
                }
                
                // Add rating if available for more compelling description
                if ($selectedItem?.rating?.overall_rating?.rating !== null && 
                    $selectedItem?.rating?.overall_rating?.rating !== undefined) {
                    descriptionText += `Betyg: ${$selectedItem.rating.overall_rating.rating}/100. `;
                }
                
                descriptionText += `Se ekonomi, avgifter, underhåll och mer - helt gratis!`;

                metaDescription = descriptionText;

                // Update URL to include BRF name (without affecting current page)
                const slug = slugify($selectedItem.brf_name);
                const newUrl = `/brf/${brfId}/${slug}`;
                
                // Note: slug is used in breadcrumbJsonLdData below

                // Always set canonical URL to the version with slug (preferred canonical)
                canonicalUrl = `https://boanalys.io${newUrl}`;

                // Update the browser URL bar without reloading the page
                // This ensures both /brf/:id and /brf/:id/:slug point to the same canonical
                if (
                    history &&
                    history.replaceState &&
                    !window.location.pathname.includes(slug)
                ) {
                    history.replaceState(null, document.title, newUrl);
                }

                // Create RealEstateOrganization structured data with enhanced local SEO
                const addressData = {
                    "@type": "PostalAddress",
                    addressLocality: $selectedItem.municipality || "",
                    addressCountry: "SE",
                };
                
                if ($selectedItem.street_address) {
                    addressData.streetAddress = $selectedItem.street_address;
                }
                if ($selectedItem.postal_code) {
                    addressData.postalCode = $selectedItem.postal_code;
                }

                jsonLdData = {
                    "@context": "https://schema.org",
                    "@type": "RealEstateOrganization",
                    name: $selectedItem.brf_name,
                    description: `Bostadsrättsförening: ${$selectedItem.brf_name}. ${$selectedItem.about || ""}`,
                    address: addressData,
                    ...($selectedItem.latitude && $selectedItem.longitude ? {
                        geo: {
                            "@type": "GeoCoordinates",
                            latitude: $selectedItem.latitude,
                            longitude: $selectedItem.longitude,
                        }
                    } : {}),
                    foundingDate: $selectedItem.registry_date || "",
                    numberOfEmployees: {
                        "@type": "QuantitativeValue",
                        value: $selectedItem.number_of_apartments || 0,
                        unitText: "lägenheter",
                    },
                    areaServed: {
                        "@type": "Place",
                        name: $selectedItem.municipality || "Sverige",
                        ...($selectedItem.latitude && $selectedItem.longitude ? {
                            geo: {
                                "@type": "GeoCoordinates",
                                latitude: $selectedItem.latitude,
                                longitude: $selectedItem.longitude,
                            }
                        } : {}),
                    },
                    ...($selectedItem.org_number
                        ? { taxID: $selectedItem.org_number }
                        : {}),
                    ...($selectedItem.built_year
                        ? { yearBuilt: $selectedItem.built_year }
                        : {}),
                    mainEntityOfPage: {
                        "@type": "WebPage",
                        "@id": canonicalUrl,
                    },
                };

                // Create Product schema for better SEO and rich results
                const productDescription = `Gratis analys och betyg för ${$selectedItem.brf_name}${$selectedItem.municipality ? ` i ${$selectedItem.municipality}` : ""}. Få detaljerad information om ekonomi, avgifter, underhåll, stadgar och energiprestanda.`;
                
                productJsonLdData = {
                    "@context": "https://schema.org",
                    "@type": "Product",
                    name: `Analys av ${$selectedItem.brf_name}`,
                    description: productDescription,
                    category: "Bostadsrättsförening Analys",
                    brand: {
                        "@type": "Brand",
                        name: "Boanalys",
                    },
                    offers: {
                        "@type": "Offer",
                        price: "0",
                        priceCurrency: "SEK",
                        availability: "https://schema.org/InStock",
                        url: canonicalUrl,
                        priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
                    },
                    about: {
                        "@type": "RealEstateOrganization",
                        name: $selectedItem.brf_name,
                        ...($selectedItem.municipality ? {
                            address: {
                                "@type": "PostalAddress",
                                addressLocality: $selectedItem.municipality,
                                addressCountry: "SE",
                            }
                        } : {}),
                    },
                    ...($selectedItem?.rating?.overall_rating?.rating !== null && 
                        $selectedItem?.rating?.overall_rating?.rating !== undefined
                        ? {
                            aggregateRating: {
                                "@type": "AggregateRating",
                                ratingValue: $selectedItem.rating.overall_rating.rating,
                                bestRating: 100,
                                worstRating: 0,
                                ratingCount: 1,
                            }
                        }
                        : {}),
                    mainEntityOfPage: {
                        "@type": "WebPage",
                        "@id": canonicalUrl,
                    },
                };

                // Create BreadcrumbList schema
                breadcrumbJsonLdData = {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        {
                            "@type": "ListItem",
                            position: 1,
                            name: "Hem",
                            item: "https://boanalys.io/",
                        },
                        {
                            "@type": "ListItem",
                            position: 2,
                            name: "BRF",
                            item: "https://boanalys.io/brf",
                        },
                        {
                            "@type": "ListItem",
                            position: 3,
                            name: $selectedItem.brf_name,
                            item: canonicalUrl,
                        },
                    ],
                };

                // FAQ Structured Data
                const faqs = [];
                let ownsTheLand = displayOwnsTheLand($selectedItem);
                if (ownsTheLand !== "Uppgifter saknas") {
                    faqs.push({
                        "@type": "Question",
                        name: `Äger ${$selectedItem.brf_name} marken?`,
                        acceptedAnswer: {
                            "@type": "Answer",
                            text:
                                ownsTheLand == "Ja"
                                    ? `Ja, föreningen äger marken.`
                                    : `Nej, föreningen innehar marken med tomträtt, vilket innebär att de hyr marken av kommunen.`,
                        },
                    });
                }
                if ($selectedItem.genuine_house_association !== null) {
                    faqs.push({
                        "@type": "Question",
                        name: `Är ${$selectedItem.brf_name} en äkta förening?`,
                        acceptedAnswer: {
                            "@type": "Answer",
                            text: $selectedItem.genuine_house_association
                                ? `Ja, föreningen är en äkta förening.`
                                : `Nej, föreningen är en oäkta förening.`,
                        },
                    });
                }
                if ($selectedItem.number_of_apartments) {
                    faqs.push({
                        "@type": "Question",
                        name: `Hur många lägenheter finns det i ${$selectedItem.brf_name}?`,
                        acceptedAnswer: {
                            "@type": "Answer",
                            text: `Det finns ${$selectedItem.number_of_apartments} lägenheter i föreningen.`,
                        },
                    });
                }
                if (
                    $selectedItem.total_amount_of_loans &&
                    $selectedItem.living_area_sqm
                ) {
                    const loanPerSqm = Math.round(
                        $selectedItem.total_amount_of_loans /
                            $selectedItem.living_area_sqm,
                    );
                    faqs.push({
                        "@type": "Question",
                        name: `Hur hög är belåningen per kvadratmeter?`,
                        acceptedAnswer: {
                            "@type": "Answer",
                            text: `Föreningens belåning ligger på cirka ${loanPerSqm} kr per kvadratmeter.`,
                        },
                    });
                }

                if (faqs.length > 0) {
                    faqJsonLdData = {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        name: `Vanliga frågor om ${$selectedItem.brf_name}`,
                        mainEntity: faqs,
                    };
                } else {
                    faqJsonLdData = null;
                }
            } else {
                console.error(
                    "selectedItem is null or missing name property after fetchBrf completed",
                );
            }
        } catch (err) {
            console.error("Error in BRF page onMount:", err);
            error = "Ett fel uppstod vid laddning av information.";
        } finally {
            loading = false;
            // Scroll to top on load
            window.scrollTo(0, 0);
        }
    }

    // Handle upload completion
    function handleUploadComplete(event) {
        console.log("Upload completed:", event.detail);
        // Refresh the BRF data to show the new analysis
        if (event.detail.coreId) {
            loadBrfData(id);
        }
    }

    // Handle upload errors
    function handleUploadError(event) {
        console.error("Upload error:", event.detail);
        // You could show a toast notification or alert here
        alert(`Uppladning misslyckades: ${event.detail.message}`);
    }

    $: if (id) {
        loadBrfData(id);
    }

    function handleShare() {
        if (navigator.share) {
            navigator
                .share({
                    title: $selectedItem?.brf_name || "BRF Information",
                    text: `Information om ${$selectedItem?.brf_name || "bostadsrättsföreningen"}`,
                    url: window.location.href,
                })
                .catch((err) => console.log("Error sharing", err));
        } else {
            navigator.clipboard
                .writeText(window.location.href)
                .then(() => {
                    alert("Länk kopierad till urklipp!");
                })
                .catch((err) => {
                    console.error("Failed to copy", err);
                    alert("Kunde inte kopiera länk");
                });
        }
    }

    function handlePrint() {
        window.print();
    }

    const API_BASE = "https://boanalys-backend-324523837899.europe-north1.run.app";
    let favouriteLoading = false;
    let globalError = "";

    $: isFavourite = $currentUser?.favourites?.includes(parseInt(id, 10));

    async function handleToggleFavourite() {
        if (!$currentUser || favouriteLoading) return;
        favouriteLoading = true;
        globalError = "";
        let errorMessage = "Något gick fel. Försök igen om en stund. Om inte det fungerar testa att logga ut och in igen. Om problemet kvarstår, kontakta oss på info@boanalys.io.";
        try {
            const res = await fetch(`${API_BASE}/users/me/favourites?core_id=${id}`, {
                method: "POST",
                credentials: "include",
                headers: { Authorization: `Bearer ${$currentUser.accessToken}` },
            });
            if (res.ok) {
                const { favourites } = await res.json();
                currentUser.set({ ...$currentUser, favourites });
            } else if (res.status === 401) {
                globalError =
                    errorMessage;
            } else {
                globalError =
                    errorMessage;
            }
        } catch (e) {
            globalError =
                errorMessage;
        } finally {
            favouriteLoading = false;
        }
    }
</script>

<svelte:head>
    <title>{pageTitle}</title>
    <meta name="description" content={metaDescription} />
    <link rel="canonical" href={canonicalUrl} />

    <!-- Open Graph / Facebook -->
    <meta property="og:type" content="website" />
    <meta property="og:url" content={canonicalUrl} />
    <meta property="og:title" content={pageTitle} />
    <meta property="og:description" content={metaDescription} />
    <meta
        property="og:image"
        content="https://boanalys.io/bo-emblem-black.png"
    />

    <!-- Twitter -->
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content={canonicalUrl} />
    <meta property="twitter:title" content={pageTitle} />
    <meta property="twitter:description" content={metaDescription} />
    <meta
        property="twitter:image"
        content="https://boanalys.io/bo-emblem-black.png"
    />

    <!-- Geo-targeting meta tags -->
    {#if $selectedItem?.municipality}
        <meta name="geo.region" content="SE" />
        <meta name="geo.placename" content={$selectedItem.municipality} />
        {#if $selectedItem?.latitude && $selectedItem?.longitude}
            <meta name="geo.position" content="{`${$selectedItem.latitude};${$selectedItem.longitude}`}" />
            <meta name="ICBM" content="{`${$selectedItem.latitude}, ${$selectedItem.longitude}`}" />
        {/if}
    {/if}

    {#if jsonLdData}
        {@html `<script type="application/ld+json">${JSON.stringify(jsonLdData)}</script>`}
    {/if}
    {#if productJsonLdData}
        {@html `<script type="application/ld+json">${JSON.stringify(productJsonLdData)}</script>`}
    {/if}
    {#if breadcrumbJsonLdData}
        {@html `<script type="application/ld+json">${JSON.stringify(breadcrumbJsonLdData)}</script>`}
    {/if}
    {#if faqJsonLdData}
        {@html `<script type="application/ld+json">${JSON.stringify(faqJsonLdData)}</script>`}
    {/if}
</svelte:head>

<main style="overflow-x: hidden; overflow-y: hidden; max-width: 1600px;">
    <Header />
    <Search />

    <div class="action-buttons action-buttons-desktop">
        {#if $currentUser}
            <button class="action-btn favourite-btn" class:is-favourite={isFavourite} on:click={handleToggleFavourite} disabled={favouriteLoading} title={isFavourite ? "Ta bort från favoriter" : "Lägg till i favoriter"}>
                <i class={isFavourite ? "fas fa-star" : "far fa-star"}></i>
            </button>
        {/if}
        <button class="action-btn" on:click={handleShare} title="Dela sidan">
            <i class="fas fa-share-alt"></i>
        </button>
        <button class="action-btn" on:click={handlePrint} title="Skriv ut">
            <i class="fas fa-print"></i>
        </button>
    </div>

    <div class="action-buttons action-buttons-mobile">
        {#if $currentUser}
            <button class="action-btn favourite-btn" class:is-favourite={isFavourite} on:click={handleToggleFavourite} disabled={favouriteLoading} title={isFavourite ? "Ta bort från favoriter" : "Lägg till i favoriter"}>
                <i class={isFavourite ? "fas fa-star" : "far fa-star"}></i>
            </button>
        {/if}
        <button class="action-btn" on:click={handleShare} title="Dela sidan">
            <i class="fas fa-share-alt"></i>
        </button>
        <button class="action-btn" on:click={handlePrint} title="Skriv ut">
            <i class="fas fa-print"></i>
        </button>
    </div>

    {#if loading}
        <div
            class="loading-container"
            style="width: 250px; margin-right: auto; margin-left: auto; margin-bottom: 80px; margin-top: 80px;"
        >
            <div class="about-text">
                <p>Laddar information...</p>
                <div style="height: 60px;margin-bottom: 30px;" class="question">
                    <Logotype />
                </div>
            </div>
        </div>
    {:else if error}
        <div class="error-container">
            <p>{error}</p>
            <p>Försök igen senare eller kontakta support.</p>
        </div>
    {:else if $selectedItem}
        <!-- New BRF Header Card -->
        <div class="brf-header-card">
            <div class="brf-title-section">
                <div class="brf-icon-container">
                    <i class="fas fa-building fa-2x"></i>
                </div>
                <h3 class="brf-name-header">{$selectedItem.brf_name}</h3>
            </div>
            <div class="brf-nav-container">
                <nav class="brf-nav">
                    <button
                        class="brf-nav-item"
                        class:active={$activeTab === "allmant"}
                        on:click={() => selectTab("allmant")}
                    >
                        Allmänt
                    </button>
                    <button
                        class="brf-nav-item"
                        class:active={$activeTab === "stadgar"}
                        on:click={() => selectTab("stadgar")}
                    >
                        Stadgar
                    </button>
                    <button
                        class="brf-nav-item"
                        class:active={$activeTab === "energi"}
                        on:click={() => selectTab("energi")}
                    >
                        Energi
                    </button>
                </nav>
                {#if $currentUser?.subscription_tier === "Pro" || $currentUser?.subscription_tier === "Plus"}
                    <nav class="brf-nav">
                        <button
                            class="brf-nav-item"
                            style="margin-bottom: 0px;"
                            class:active={$activeTab === "ekonomisk_plan"}
                            on:click={() => selectTab("ekonomisk_plan")}
                        >
                            Ekonomisk Plan
                        </button>
                    </nav>
                {/if}
            </div>
        </div>

        <!-- Semantic Key Facts for GEO/LLMs (Visible to users too) -->
        {#if $selectedItem}
            <article class="key-facts-section" aria-label="Nyckeldata">
                <dl class="key-facts-list">
                    {#if $selectedItem?.org_number}
                        <div class="fact-item">
                            <dt>Organisationsnummer</dt>
                            <dd>
                                {$selectedItem.org_number || "Ej tillgängligt"}
                            </dd>
                        </div>
                    {/if}
                    {#if $selectedItem?.built_year}
                        <div class="fact-item">
                            <dt>Byggår</dt>
                            <dd>
                                {$selectedItem.built_year || "Ej tillgängligt"}
                            </dd>
                        </div>
                    {/if}
                    {#if $selectedItem?.number_of_apartments}
                        <div class="fact-item">
                            <dt>Antal lägenheter</dt>
                            <dd>
                                {$selectedItem.number_of_apartments ||
                                    "Ej tillgängligt"}
                            </dd>
                        </div>
                    {/if}
                    {#if $selectedItem?.municipality}
                        <div class="fact-item">
                            <dt>Kommun</dt>
                            <dd>
                                {$selectedItem.municipality ||
                                    "Ej tillgängligt"}
                            </dd>
                        </div>
                    {/if}
                </dl>
            </article>
        {/if}

    <div class="analyze-info">
            {#if $activeTab === "allmant"}
                <div class="container">
                    {#if $selectedItem?.analyzed}
                        <General
                            on:uploadComplete={handleUploadComplete}
                            on:uploadError={handleUploadError}
                        />
                        <Chatbot />
                        <Rating />

                        {#if $selectedItem?.latitude && $selectedItem?.longitude}
                            <div class="property-map-wrapper">
                                <div class="property-column">
                                    <Property />
                                </div>
                                <div class="map-column">
                                    <PropertyMap />
                                </div>
                            </div>
                        {:else}
                            <Property />
                        {/if}

                        <Maintenance />
                        <EconomyAndIncomes />
                        <Company />
                        <ReaVinstCalculator />
                        {#if showHistoryCharts}
                            <div class="history-charts-container">
                                {#if showExpensesHistory}
                                    <ExpensesHistory />
                                {/if}
                                {#if showIncomeHistory}
                                    <IncomeHistory />
                                {/if}
                                {#if showLoanFeeHistory}
                                    <LoanFeeHistory />
                                {/if}
                            </div>
                        {/if}
                    {:else}
                        <div class="unanalysed-section">
                            <Company />
                            <UploadRedovisning />
                        </div>
                    {/if}
                    {#if $currentUser?.subscription_tier === "Pro" || $currentUser?.subscription_tier === "Plus"}
                        {#if !$selectedItem?.analyzed === true && $selectedItem?.redovisning_file_name != null && $selectedItem?.redovisning_file_name !== ""}
                            <div class="pdf-container">
                                <h3>Årsredovisning</h3>
                                <iframe
                                    class="iframe"
                                    title="redovisning"
                                    src={getFileName(
                                        $selectedItem.redovisning_file_name,
                                    )}
                                    frameborder="0"
                                ></iframe>
                            </div>
                        {/if}
                        {#if $selectedItem?.analyzed === true && $selectedItem?.file_name != null && $selectedItem?.file_name !== ""}
                            <div class="pdf-container">
                                <h3>Årsredovisning</h3>
                                <iframe
                                    class="iframe"
                                    title="redovisning"
                                    src={getFileName($selectedItem.file_name)}
                                    frameborder="0"
                                ></iframe>
                            </div>
                        {/if}
                    {/if}
                </div>
            {/if}

            {#if $activeTab === "stadgar"}
                <div class="full-width-section stadgar-content-tab">
                    {#if $selectedItem?.stadgar_id !== null}
                        <div class="container">
                            <Stadgar />
                            <StadgarFAQ />
                        </div>
                    {/if}
                    <AnalyzeStadgar />
                </div>
            {/if}

            {#if $activeTab === "energi"}
                <div class="full-width-section energi-content-tab">
                    {#if $selectedItem?.energy_declaration_id !== null}
                        <div class="container">
                            <Energy />
                        </div>
                    {/if}
                    <AnalyzeEnergy />
                </div>
            {/if}

            {#if $activeTab === "ekonomisk_plan"}
                {#if $selectedItem.financial_plan_id}
                    <div class="full-width-section">
                        <FinancialPlan />
                    </div>
                {:else if $currentUser?.subscription_tier === "Pro" || $currentUser?.subscription_tier === "Plus"}
                    <div class="full-width-section ekonomisk-plan-content-tab">
                        <AnalyzeFinancialPlan />
                    </div>
                {/if}
            {/if}
        </div>
    {:else}
        <div class="error-container">
            <p>Ingen information hittades för denna bostadsrättsförening.</p>
        </div>
    {/if}
    {#if globalError}
        <div class="global-error-banner">
            <div class="global-error-content">
                <span>{globalError}</span>
                <button
                    class="global-error-close"
                    type="button"
                    on:click={() => (globalError = "")}
                    aria-label="Stäng felmeddelande"
                >
                    &times;
                </button>
            </div>
        </div>
    {/if}
</main>

<style>
    main {
        max-width: 1800px;
        margin: 0 auto;
    }

    /* Base container styles */
    .container {
        display: grid;
        gap: 20px;
    }

    /* Desktop layout (992px and up) */
    @media (min-width: 992px) {
        .container {
            grid-template-columns: 1fr 2fr 1fr; /* First row: 1:2:1 */
        }

        /* First row components */
        .container > :global(.box:nth-child(1)), /* General */
        .container > :global(.box:nth-child(2)), /* Chatbot */
        .container > :global(.box:nth-child(3))  /* Rating */ {
            width: 100%;
        }

        /* Property and Map wrapper */
        .property-map-wrapper {
            display: flex;
            gap: 20px;
            grid-column: 1 / -1;
        }

        .property-map-wrapper > .property-column,
        .property-map-wrapper > .map-column {
            flex: 1;
        }
    }

    /* Tablet layout (768px to 991px) */
    @media (min-width: 768px) and (max-width: 991px) {
        .container {
            grid-template-columns: 1fr;
        }

        .property-map-wrapper {
            display: flex;
            gap: 20px;
        }

        .property-map-wrapper > .property-column,
        .property-map-wrapper > .map-column {
            flex: 1;
        }
    }

    /* Mobile layout (below 768px) */
    @media (max-width: 767px) {
        .container {
            grid-template-columns: 1fr;
        }

        .property-map-wrapper {
            display: flex;
            flex-direction: column;
            gap: 20px;
        }

        .property-map-wrapper > .property-column,
        .property-map-wrapper > .map-column {
            width: 100%;
        }

        /* Ensure map has a good height on mobile */
        .property-map-wrapper > .map-column > :global(*) {
            height: 300px;
            min-height: 300px;
        }
    }

    /* Common styles for all screen sizes */
    .container > :global(.box),
    .container > :global(app-uploadredovisning) {
        width: 100%;
    }

    /* PDF container always full width */
    .pdf-container {
        grid-column: 1 / -1;
        width: 100%;
    }

    /* Styles for the new BRF header card */
    .brf-header-card {
        background-color: #fff;
        border-radius: 8px;
        padding: 20px;
        padding-bottom: 15px;
        margin: 50px auto 20px auto; /* Keeps top/bottom margin, auto for left/right centers it */
        width: fit-content; /* Makes the card width fit its content */
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        max-width: 700px;
    }

    .brf-title-section {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
    }

    .brf-icon-container {
        margin-right: 15px;
        color: var(--box-title-color); /* Dark blueish color for icon */
    }

    .brf-icon-container .fas {
        /* If using Font Awesome */
        font-size: 28px; /* Adjust size */
        line-height: 1; /* Ensure proper alignment */
    }

    .brf-name-header {
        font-size: 1.8em;
        font-weight: bold;
        color: var(--box-title-color);
        margin: 0;
    }

    .brf-nav-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 0 0 0 0;
    }

    .brf-nav {
        display: flex;
        justify-content: center; /* Center the tab items */
        border-top: 2px solid #eee; /* Light grey line above tabs container */
        padding-top: 5px; /* Add some padding above the tabs */
        width: 100%;
    }

    .brf-nav:last-child {
        border-top: none;
        padding-top: 0;
    }

    .brf-nav-item {
        background: none;
        border: none;
        padding: 10px 15px;
        cursor: pointer;
        font-size: 1em;
        color: #7f8c8d; /* Greyish color for inactive tabs */
        position: relative;
        font-weight: 500;
        margin-right: 10px; /* Spacing between tabs */
    }
    .brf-nav-item:last-child {
        margin-right: 0;
    }

    .brf-nav .brf-nav-item:last-child {
        margin-right: 0; /* Ensure last item in each row has no right margin */
    }

    .brf-nav-item.active {
        color: var(--hover); /* Blue color for active tab text */
        font-weight: bold;
    }

    .brf-nav-item.active::after {
        content: "";
        position: absolute;
        bottom: -1px; /* Align with the nav border-bottom */
        left: 0;
        width: 100%;
        height: 3px;
        background-color: var(--hover); /* Blue underline */
        border-radius: 1.5px;
    }

    /* Hover effects for non-active tabs */
    .brf-nav-item:not(.active):hover {
        color: var(
            --hover
        ); /* Change text color on hover to match active tab color */
    }

    .brf-nav-item:not(.active)::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: -1px; /* Position the underline slightly below the text */
        width: 0; /* Initial width of the underline is 0 */
        height: 3px; /* Thickness of the underline */
        background-color: var(
            --hover
        ); /* Underline color, matches active tab */
        transition: width 0.3s ease; /* Smooth transition for the width */
        border-radius: 1.5px;
    }

    .brf-nav-item:not(.active):hover::after {
        width: 100%; /* Expand underline to full width on hover */
    }

    .analyze-info {
        margin-bottom: 20px;
    }

    /* Ensure tab content sections are distinct */
    .analyze-info > .full-width-section {
        margin-top: 0; /* Reset top margin if .analyze-info provides it */
        padding-top: 20px; /* Add padding if needed */
    }

    /* Added style for full-width sections */
    .full-width-section {
        grid-column: 1 / -1; /* Make the section span all columns */
        width: 100%;
        margin-top: 20px;
        margin-bottom: 30px;
        padding-top: 20px;
    }

    /* Reset any grid styling in the nested container */
    .full-width-section .container {
        display: block; /* Override grid display */
        width: 100%;
    }

    /* Make each component in stadgar/energy sections take full width */
    .full-width-section .container > :global(*) {
        width: 100%;
        margin-bottom: 20px;
    }

    /* Allow Energy component to use its internal grid layout properly */
    .energi-content-tab .container {
        max-width: 100%;
        display: block;
    }

    .energi-content-tab .container > :global(.energy-wrapper) {
        width: 100%;
        max-width: none;
    }

    /* Override the Energy component's grid to ensure at least 3 columns on larger screens */
    @media (min-width: 900px) {
        .energi-content-tab :global(.energy-content-grid) {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    /* Stadgar content tab styling */
    .stadgar-content-tab .container {
        max-width: 100%;
        display: block;
    }

    .stadgar-content-tab .container > :global(*) {
        width: 100%;
    }

    /* Styles for the section when BRF is not analyzed */
    .unanalysed-section {
        grid-column: 1 / -1; /* Span all grid columns */
        display: flex; /* Use flexbox for centering */
        flex-direction: column; /* Stack children vertically */
        align-items: center; /* Center children horizontally */
        gap: 20px; /* Space between children */
        width: 100%; /* Take full width of the grid area */
        margin-top: 20px; /* Add some space above */
        margin-bottom: 30px; /* Add some space below */
    }

    /* Style the components within the unanalysed section */
    .unanalysed-section > :global(*) {
        /* Make each component take roughly half the width on larger screens */
        width: 50%;
        /* Optional: Set a max-width if needed, e.g., for very wide screens */
        /* max-width: 900px; */
        /* Ensure components have a minimum reasonable width */
        min-width: 300px; /* Adjust as needed */
        box-sizing: border-box; /* Ensure padding/border are included in the width */
    }

    /* Semantic Key Facts Styling */
    .key-facts-section {
        max-width: 700px;
        margin: 0 auto 20px auto;
        padding: 0 20px;
    }

    .key-facts-list {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        justify-content: center;
        margin: 0;
        padding: 0;
    }

    .fact-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: #ffffff;
        padding: 12px 20px;
        border-radius: 12px;
        font-size: 0.9em;
        box-shadow:
            0 2px 4px 0 rgba(4, 4, 52, 0.06),
            0 0 2px 0 rgba(91, 95, 107, 0.24);
        transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
    }

    .fact-item:hover {
        transform: translateY(-2px);
        box-shadow:
            0 8px 12px 0 rgba(4, 4, 52, 0.1),
            0 0 4px 0 rgba(91, 95, 107, 0.3);
    }

    .fact-item dt {
        font-weight: 600;
        color: #7f8c8d;
        font-size: 0.8em;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .fact-item dd {
        margin: 0;
        font-weight: 500;
        color: #2c3e50;
    }

    /* Adjust width for smaller screens for better layout */
    @media (max-width: 991px) {
        /* Apply on tablet and potentially wider mobile */
        .unanalysed-section > :global(*) {
            width: 75%; /* Use a larger percentage on medium screens */
        }
    }

    @media (max-width: 767px) {
        /* Apply on standard mobile */
        .unanalysed-section > :global(*) {
            width: 100%; /* Take full width on small screens */
            min-width: unset; /* Remove min-width constraint */
        }
    }

    /* Style the history charts container */
    .history-charts-container {
        grid-column: 1 / -1; /* Span full width of the parent grid */
        display: flex;
        flex-wrap: wrap; /* Allow items to wrap to the next line */
        justify-content: center; /* Center items horizontally */
        gap: 20px; /* Space between charts */
    }

    /* Style the individual history chart components within the container */
    .history-charts-container > :global(*) {
        flex-grow: 1;
        /* Each item tries to be 50% wide, minus half the gap */
        flex-basis: calc(50% - 10px);
        /* A reasonable minimum width */
        min-width: 350px;
        /* Set max width based on user request */
        max-width: 800px;
        box-sizing: border-box;
    }

    /* On smaller screens, make them full width */
    @media (max-width: 767px) {
        .history-charts-container > :global(*) {
            flex-basis: 100%;
            min-width: 280px; /* Smaller min-width for mobile */
            max-width: 100%; /* Override max-width for mobile */
        }
    }

    @media (max-width: 600px) {
        .brf-header-card {
            margin-left: auto;
            margin-right: auto;
        }
    }

    .global-error-banner {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        background: #fef2f2;
        border-top: 1px solid #fecaca;
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
        z-index: 1100;
        padding: 10px 16px;
    }

    .global-error-content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        font-size: 0.95rem;
        color: #b91c1c;
    }

    .global-error-close {
        border: none;
        background: transparent;
        color: #b91c1c;
        font-size: 1.4rem;
        cursor: pointer;
        padding: 0 4px;
        line-height: 1;
    }

    .global-error-close:hover {
        color: #7f1d1d;
    }

    .action-buttons {
        display: flex;
        gap: 10px;
    }

    .action-buttons-desktop {
        position: fixed;
        top: 40px;
        right: 20px;
        z-index: 1000;
    }

    .action-buttons-mobile {
        display: none;
        justify-content: center;
        margin: 10px auto 10px auto;
        max-width: 600px;
    }

    .action-btn {
        width: 44px;
        height: 44px;
        border-radius: 8px;
        border: 1px solid #e0e0e0;
        background: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--box-title-color);
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .action-btn:hover {
        background: var(--hover);
        color: #fff;
        border-color: var(--hover);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    .action-btn i {
        font-size: 18px;
    }
    .favourite-btn.is-favourite i {
        color: #ec776a;
    }

    @media (max-width: 640px) {
        .action-buttons-desktop {
            display: none;
        }

        .action-buttons-mobile {
            display: flex;
        }

        .action-btn {
            width: 40px;
            height: 40px;
        }

        .action-btn i {
            font-size: 16px;
        }

        .brf-header-card {
            margin-top: 0px;
        }
    }
</style>
